import { adjustCanvasSizeToMaxHandledArea } from 'common/utilities/browser/canvasSize/canvasSize';
import useScroll from 'common/utilities/hooks/useScroll/useScroll';
import type { ScatterPlot } from 'dataAnalyzer/api/dataAnalyzerApiClient';
import { TEST_SCROLLABLE_CHART } from 'dataAnalyzer/components/ChartView/ChartView';
import { createRef, useCallback, useEffect } from 'react';

import { LEGEND_WIDTH } from './Chart';

const ITEMS_SPACE_PIXELS = 20;
export const SCROLLBAR_WIDTH = 18;

const useScrollableChart = (
  plotData: ScatterPlot,
  scrollable: boolean,
  availableChartHeight: number,
  availableChartWidth: number,
) => {
  let chartHeight = ITEMS_SPACE_PIXELS * (plotData.yUniqueValues || []).length + ITEMS_SPACE_PIXELS;
  if (availableChartHeight > chartHeight) chartHeight = availableChartHeight - LEGEND_WIDTH - SCROLLBAR_WIDTH;
  let chartWidth = ITEMS_SPACE_PIXELS * (plotData.xUniqueValues || []).length + ITEMS_SPACE_PIXELS;
  if (availableChartWidth > chartWidth) chartWidth = availableChartWidth - LEGEND_WIDTH - SCROLLBAR_WIDTH;

  if (TEST_SCROLLABLE_CHART) {
    chartHeight = 600;
    chartWidth = 4000;
  }

  const adjustedSize = adjustCanvasSizeToMaxHandledArea(chartWidth, chartHeight);
  chartWidth = adjustedSize.width;
  chartHeight = adjustedSize.height;

  const availableChartWidthWithOptionalScrollbar = availableChartWidth - (scrollable ? SCROLLBAR_WIDTH : 0);
  const availableChartHeighthWithOptionalScrollbar = availableChartHeight - (scrollable ? SCROLLBAR_WIDTH : 0);

  const scrollableRef = createRef<HTMLDivElement>();
  const chartXAxisRef = createRef<HTMLDivElement>();
  const chartYAxisRef = createRef<HTMLDivElement>();
  const scroll = useScroll(scrollableRef, true);

  /**
   * A scrollable chart has both axes in a fixed position and the content (axis canvas)
   * is scrolled matching the current chart scroll position
   */
  const handleScroll = useCallback((axis?: 'x' | 'y') => {
    if (scrollableRef.current && chartXAxisRef.current && chartYAxisRef.current) {
      if (axis === 'y') {
        chartYAxisRef.current.scrollTop = scrollableRef.current.scrollTop;
      }
      if (axis === 'x') {
        chartXAxisRef.current.scrollLeft = scrollableRef.current.scrollLeft;
      }
    }
  }, [chartXAxisRef, chartYAxisRef, scrollableRef]);

  useEffect(() => {
    if (scrollable) {
      handleScroll(scroll.scrollingAxis);
    }
  }, [handleScroll, scroll, scrollable]);

  return {
    scrollableRef,
    chartXAxisRef,
    chartYAxisRef,
    chartHeight,
    chartWidth,
    availableChartWidthWithOptionalScrollbar,
    availableChartHeighthWithOptionalScrollbar,
  };
};

export default useScrollableChart;
